import { DateTime } from 'luxon';
import { getShiftCodeAt } from '../../../../shared/services/shifts/shifts.ts';
import { OeeRow } from '../../../types/sharedTypeImpl.ts';
import { getGraphColorSimple } from '../../../utils/liveGraphColors.ts';
import { Bar, BarGraphData, BarGroup } from '../barGraphData.ts';
import { PeriodOption, ReportSettings } from '../report-settings/reportSettings.ts';


interface OeeRow2 extends OeeRow {
    isOnWeekend: boolean;
}


export function prepareAvailabilityData(
    shiftData: OeeRow[],
    reportSettings: ReportSettings,
): BarGraphData {
    const oeeDataTransformed = transformInputs(shiftData);
    const oeeDataFiltered = filterInputs(oeeDataTransformed, reportSettings);

    const barGroups = reportSettings.periodsInRange.map(period =>
        getBarGroup(oeeDataFiltered, period, reportSettings)
    );

    const maxValue = reportSettings.mainValue === 'Sum'
        ? Math.max(...barGroups.flatMap(barGroup =>
            barGroup.bars.flatMap(bar =>
                bar.sections.map(section =>
                    section.value))))
        : 1;

    const targetValue = reportSettings.mainValue === 'Percentage'
        ? parseFloatOrUndefined(reportSettings.availabilityTargetValue)
        : undefined;

    return {
        barGroups,
        maxValue,
        targetValue,
    };
}

function transformInputs(shiftData: OeeRow[]): OeeRow2[] {
    return shiftData.map(it => ({
        ...it,
        isOnWeekend: it.shiftCode === 'W',
        shiftCode: getShiftCodeAt(it.start, false),//Override shift code to convert W shifts to A/B/N
    }));
}

function filterInputs(shiftData: OeeRow2[], reportSettings: ReportSettings): OeeRow2[] {
    return shiftData
        .filter(it => reportSettings.visibleShifts.includes(it.shiftSlot))
        .filter(it => it.isOnWeekend && reportSettings.weekends || !it.isOnWeekend && reportSettings.workdays)
        .filter(it => isInPeriod(it, reportSettings.first.start, reportSettings.last.end));
}

function isInPeriod(shiftOeeRow: OeeRow2, start: Date, end: Date): boolean {
    // Filtering by date and by the shift start/end time must both work correctly.
    // Make sure that night shifts belong to the date that they started on.
    return shiftOeeRow.start >= start && shiftOeeRow.start < end;
}

// A bar group correspons to one unit of time. It is either one bar or separated A/B shift bars
function getBarGroup(shiftData: OeeRow2[], period: PeriodOption, reportSettings: ReportSettings): BarGroup {
    const oeeDataInPeriod = shiftData.filter((it) => isInPeriod(it, period.start, period.end));

    const bars: Bar[] = [];
    if (reportSettings.shiftsDisplay === 'Separated') {
        const oeeShifts = splitABNShifts(oeeDataInPeriod, reportSettings);
        const aShiftBar = getBar(oeeShifts[0], 'A', reportSettings);
        const bShiftBar = getBar(oeeShifts[1], 'B', reportSettings);
        const nShiftBar = getBar(oeeShifts[2], 'N', reportSettings);
        const firstShiftA = isFirstShiftA(period);
        bars.push(firstShiftA ? aShiftBar : bShiftBar);
        bars.push(firstShiftA ? bShiftBar : aShiftBar);
        if (reportSettings.visibleShifts.includes('night')) {
            bars.push(nShiftBar);
        }
    } else {
        bars.push(getBar(oeeDataInPeriod, '', reportSettings));
    }

    return {
        periodLabel: period.periodLabel,
        bars,
    };
}

function splitABNShifts(shiftData: OeeRow2[], reportSettings: ReportSettings): OeeRow2[][] {
    const rowsInShiftA = [];
    const rowsInShiftB = [];
    const rowsInShiftN = [];
    for (const oeeRow of shiftData) {
        const shiftCode = reportSettings.weekends ? getShiftCodeAt(oeeRow.start, false) : oeeRow.shiftCode;
        if (shiftCode === 'A') {
            rowsInShiftA.push(oeeRow);
        } else if (shiftCode === 'B') {
            rowsInShiftB.push(oeeRow);
        } else if (shiftCode === 'N') {
            rowsInShiftN.push(oeeRow);
        }
    }
    return [rowsInShiftA, rowsInShiftB, rowsInShiftN];
}

function getBar(shiftData: OeeRow2[], barLabel: string, reportSettings: ReportSettings): Bar {
    const { hours, percentage } = aggregateAvailability(shiftData);

    return {
        sections: [
            {
                value: reportSettings.mainValue === 'Sum' ? hours : percentage,
                color: getGraphColorSimple(true, percentage),
                labels: [
                    { position: 'above-bottom', text: barLabel },
                    { position: 'below-top', text: percentage.toFixed(2) },
                    { position: 'below-below-top', text: hours.toFixed(0) + 'h' },
                ],
            },
        ],
    };
}

function aggregateAvailability(shiftData: OeeRow2[]): {
    percentage: number;
    hours: number;
} {
    const totalProductionTime = shiftData.reduce((acc, it) => acc + it.productionTime, 0);
    const totalStopTime = shiftData.reduce((acc, it) => acc + it.stopTime, 0);

    const percentage = totalProductionTime + totalStopTime === 0 ? 0 : totalProductionTime / (totalProductionTime + totalStopTime);
    const hours = totalProductionTime / 3600_000;

    return {
        percentage,
        hours,
    };
}

function parseFloatOrUndefined(value: string | undefined): number | undefined {
    if (value === undefined) return undefined;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
}

function isFirstShiftA(period: PeriodOption): boolean {
    const morningShiftTime = DateTime.fromJSDate(period.start).set({ hour: 10, minute: 0, second: 0, millisecond: 0 }); // Middle of morning shift
    const morningShiftCode = getShiftCodeAt(morningShiftTime);
    return morningShiftCode === 'A';
}
